import type {
  AgentGraphCommand,
  GraphEdge,
  GraphEntityType,
  GraphNode,
  GraphRelationType,
  GraphVisualizationFrame
} from "@salesko/contracts";
import {
  stableConfidenceTag,
  stableGraphFrameAbout,
  stableGraphFrameSummary,
  stableGraphFrameTag,
  stableGraphFrameTitle,
  stableReviewFocusReason,
  stableTimelineMomentSummary
} from "./stableText";

const entityTypes: GraphEntityType[] = ["person", "organisation", "event", "opportunity", "note"];
const typeColor = {
  event: "#f59f26",
  note: "#9ca3af",
  opportunity: "#f5c84c",
  organisation: "#2ed395",
  person: "#2f8cff"
} satisfies Record<GraphEntityType, string>;

export type ReviewItem = {
  nodeId: string;
  label: string;
  reasons: ReviewReason[];
};

export type ReviewReason =
  | { type: "agent_flagged" }
  | { type: "low_confidence"; confidencePercent: number }
  | { type: "provider"; text: string };

export function reviewItemsForFrame(frame: GraphVisualizationFrame): ReviewItem[] {
  const items = new Map<string, ReviewItem>();
  const reviewCommands = frame.commands.filter(isRequestReviewCommand);

  for (const node of frame.graph.nodes) {
    if (node.data.reviewStatus === "accepted") {
      continue;
    }

    const reasons: ReviewReason[] = [];
    if (node.visual.emphasis === "review") {
      reasons.push({ type: "agent_flagged" });
    }
    if (node.confidence < 0.6) {
      reasons.push({ type: "low_confidence", confidencePercent: Math.round(node.confidence * 100) });
    }
    for (const command of reviewCommands) {
      if (command.nodeIds.includes(node.id)) {
        reasons.push({ type: "provider", text: command.reason });
      }
    }

    if (reasons.length > 0) {
      items.set(node.id, {
        nodeId: node.id,
        label: node.label,
        reasons: uniqueReviewReasons(reasons)
      });
    }
  }

  return Array.from(items.values());
}

export function reviewItemsForNode(frame: GraphVisualizationFrame, node: GraphNode): ReviewItem[] {
  return reviewItemsForFrame(frame).filter((item) => item.nodeId === node.id);
}

export function acceptReviewCandidate(frame: GraphVisualizationFrame, nodeId: string): GraphVisualizationFrame {
  const nextNodes = frame.graph.nodes.map((node) => {
    if (node.id !== nodeId) {
      return node;
    }

    return {
      ...node,
      data: { ...node.data, reviewStatus: "accepted" },
      visual: {
        ...node.visual,
        emphasis: node.visual.emphasis === "review" ? "normal" : node.visual.emphasis
      }
    };
  });

  return rebuildFrameReviewState({
    ...frame,
    graph: {
      nodes: nextNodes,
      edges: frame.graph.edges
    },
    commands: rewriteReviewCommands(frame.commands, nodeId)
  }, nodeId);
}

export function discardReviewCandidate(frame: GraphVisualizationFrame, nodeId: string): GraphVisualizationFrame {
  const nextNodes = frame.graph.nodes.filter((node) => node.id !== nodeId);
  const nextEdges = frame.graph.edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
  const selectedNodeId = frame.ui.detailPanel.selectedNodeId === nodeId
    ? nextNodes[0]?.id
    : frame.ui.detailPanel.selectedNodeId;

  return rebuildFrameReviewState({
    ...frame,
    graph: {
      nodes: nextNodes,
      edges: nextEdges
    },
    commands: rewriteReviewCommands(
      frame.commands.filter((command) => {
        if (command.type === "upsert_node") {
          return command.node.id !== nodeId;
        }
        if (command.type === "upsert_edge") {
          return command.edge.source !== nodeId && command.edge.target !== nodeId;
        }
        if (command.type === "focus_node") {
          return command.nodeId !== nodeId;
        }
        return true;
      }),
      nodeId
    )
  }, selectedNodeId);
}

function rebuildFrameReviewState(frame: GraphVisualizationFrame, selectedNodeId?: string): GraphVisualizationFrame {
  const selectedNode = frame.graph.nodes.find((node) => node.id === selectedNodeId) ?? frame.graph.nodes[0] ?? null;
  const edges = dedupeEdges(frame.graph.edges);
  const commands = rebuildCommands(frame.commands, frame.graph.nodes, edges, selectedNode?.id);
  const nextFrame: GraphVisualizationFrame = {
    ...frame,
    graph: {
      nodes: frame.graph.nodes,
      edges
    },
    timeline: timelineFromGraph(frame.graph.nodes, edges),
    camera: {
      ...frame.camera,
      ...(selectedNode ? { focusNodeId: selectedNode.id, mode: "focused" as const } : { mode: "flat" as const })
    },
    ui: {
      ...frame.ui,
      filters: entityTypes.map((entityType) => ({
        entityType,
        enabled: frame.ui.filters.find((filter) => filter.entityType === entityType)?.enabled ?? true,
        color: frame.ui.filters.find((filter) => filter.entityType === entityType)?.color ?? typeColor[entityType],
        count: frame.graph.nodes.filter((node) => node.type === entityType).length
      })),
      detailPanel: {
        selectedNodeId: selectedNode?.id,
        title: selectedNode?.label ?? stableGraphFrameTitle(),
        subtitle: selectedNode?.summary,
        tags: selectedNode ? [selectedNode.type, stableConfidenceTag(selectedNode.confidence)] : [stableGraphFrameTag()],
        about: selectedNode?.summary ?? stableGraphFrameAbout(),
        linkedNodeIds: selectedNode ? linkedNodeIdsFor(selectedNode.id, edges) : []
      },
      stats: {
        events: frame.graph.nodes.filter((node) => node.type === "event").length,
        opportunities: frame.graph.nodes.filter((node) => node.type === "opportunity").length,
        organisations: frame.graph.nodes.filter((node) => node.type === "organisation").length,
        people: frame.graph.nodes.filter((node) => node.type === "person").length
      }
    },
    commands,
    facets: {
      entityTypes: unique(frame.graph.nodes.map((node) => node.type)),
      relationTypes: unique(edges.map((edge) => edge.type)),
      warnings: frame.facets.warnings.filter((warning) => !warning.toLowerCase().includes("review"))
    },
    summary: stableGraphFrameSummary(frame.graph.nodes, edges),
    needsReview: false
  };

  return {
    ...nextFrame,
    needsReview: reviewItemsForFrame(nextFrame).length > 0
  };
}

function rewriteReviewCommands(commands: AgentGraphCommand[], nodeId: string): AgentGraphCommand[] {
  const next: AgentGraphCommand[] = [];
  for (const command of commands) {
    if (!isRequestReviewCommand(command)) {
      next.push(command);
      continue;
    }
    const nodeIds = command.nodeIds.filter((id) => id !== nodeId);
    if (nodeIds.length > 0) {
      next.push({ ...command, nodeIds });
    }
  }
  return next;
}

function rebuildCommands(
  commands: AgentGraphCommand[],
  nodes: GraphNode[],
  edges: GraphEdge[],
  selectedNodeId: string | undefined
): AgentGraphCommand[] {
  const reviewCommands = commands.filter(isRequestReviewCommand);
  const rebuilt: AgentGraphCommand[] = [];
  for (const node of nodes) {
    rebuilt.push({ type: "upsert_node", node });
  }
  for (const edge of edges) {
    rebuilt.push({ type: "upsert_edge", edge });
  }
  if (selectedNodeId) {
    rebuilt.push({ type: "focus_node", nodeId: selectedNodeId, reason: stableReviewFocusReason() });
  }
  rebuilt.push(...reviewCommands);
  return rebuilt;
}

function dedupeEdges(edges: GraphEdge[]) {
  const seen = new Set<string>();
  return edges.filter((edge) => {
    const key = `${edge.source}__${edge.type}__${edge.target}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function timelineFromGraph(nodes: GraphNode[], edges: GraphEdge[]): GraphVisualizationFrame["timeline"] {
  return nodes
    .filter((node) => node.type === "event")
    .map((event) => ({
      id: `timeline-${event.id}`,
      label: event.label,
      ...(event.occurredAt ? { timestamp: event.occurredAt } : {}),
      nodeIds: [event.id, ...linkedNodeIdsFor(event.id, edges)],
      summary: event.summary ?? stableTimelineMomentSummary()
    }));
}

function linkedNodeIdsFor(nodeId: string, edges: GraphEdge[]) {
  return unique(
    edges
      .filter((edge) => edge.source === nodeId || edge.target === nodeId)
      .map((edge) => (edge.source === nodeId ? edge.target : edge.source))
  );
}

function isRequestReviewCommand(command: AgentGraphCommand): command is Extract<AgentGraphCommand, { type: "request_review" }> {
  return command.type === "request_review";
}

function unique<T extends GraphEntityType | GraphRelationType | string>(values: T[]) {
  return [...new Set(values)];
}

function uniqueReviewReasons(reasons: ReviewReason[]) {
  const seen = new Set<string>();
  return reasons.filter((reason) => {
    const key = reviewReasonKey(reason);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function reviewReasonKey(reason: ReviewReason) {
  switch (reason.type) {
    case "agent_flagged":
      return reason.type;
    case "low_confidence":
      return `${reason.type}:${reason.confidencePercent}`;
    case "provider":
      return `${reason.type}:${reason.text}`;
  }
}
